import { useState, useEffect, useRef } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Edit02Icon as EditIcon, XCloseIcon as XClose, CheckIcon } from '@untitledui-icons/react/line'
import { Button } from '@/components/base/buttons'
import { Input } from '@/components/ui/Input'
import { Checkbox } from '@/components/ui/Checkbox'
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter } from '@/components/ui/Modal'
import { LoadingSpinner } from '@/components/common'
import { usePhoneNumber, usePhoneNumbersList } from '@/hooks/api/usePhoneNumbers'
import { usePermissions } from '@/hooks/api'
import { cn } from '@/lib/utils'

interface PhoneNumberInputProps {
  assistantId?: number
}

export function PhoneNumberInput({ assistantId }: PhoneNumberInputProps) {
  const { t } = useTranslation()
  const { control, setValue, getValues, formState: { errors } } = useFormContext()
  const { canManage } = usePermissions()
  // Get error message - use translation if available, fallback to Zod message
  const phoneNumberError = errors.phone_number_id 
    ? t('app.validation.phone_number_required')
    : undefined
  const isAdminOrPartner = canManage

  // State
  const [isEditing, setIsEditing] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [waAckChecked, setWaAckChecked] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Watch the phone_number_id from the form
  const phoneNumberId = useWatch({ control, name: 'phone_number_id' })

  // Fetch current phone number details
  const { data: currentPhoneNumber, isLoading: isLoadingCurrent } = usePhoneNumber(phoneNumberId)

  // Fetch available phone numbers for autocomplete
  const { data: phoneNumbers = [], isLoading: isLoadingList } = usePhoneNumbersList({
    search: searchTerm,
    assistantId,
    enabled: isEditing,
  })

  // Handle click outside to close dropdown
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Open dropdown when editing starts
  useEffect(() => {
    if (isEditing) {
      setIsOpen(true)
      // Pre-fill search with current number
      if (currentPhoneNumber?.number) {
        setSearchTerm(currentPhoneNumber.number)
      }
      // Focus the input
      setTimeout(() => inputRef.current?.focus(), 100)
    }
  }, [isEditing, currentPhoneNumber])

  const isWaOnline = currentPhoneNumber?.whatsapp_status === 'online'

  const handleEditClick = () => {
    if (phoneNumberId) {
      // Show confirmation dialog if there's an existing number
      setWaAckChecked(false)
      setShowConfirm(true)
    } else {
      // No existing number, go straight to editing
      setIsEditing(true)
    }
  }

  const handleConfirm = () => {
    setShowConfirm(false)
    setWaAckChecked(false)
    // Clear the current phone number so the form becomes dirty
    setValue('phone_number_id', undefined, { shouldDirty: true, shouldValidate: true })
    setIsEditing(true)
  }

  const handleCancel = () => {
    setIsEditing(false)
    setIsOpen(false)
    setSearchTerm('')
  }

  const handleSelectPhoneNumber = (phoneNumber: { id: number; number: string }) => {
    setValue('phone_number_id', phoneNumber.id, { shouldDirty: true, shouldValidate: true })
    setIsEditing(false)
    setIsOpen(false)
    setSearchTerm('')
  }

  // If not admin/partner, show read-only
  if (!isAdminOrPartner) {
    return (
      <div>
        <div className={cn(
          "rounded-lg border px-4 py-3",
          phoneNumberError 
            ? "border-error-500 bg-error-50 dark:border-error-500 dark:bg-error-900/10"
            : "border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800"
        )}>
          {isLoadingCurrent ? (
            <LoadingSpinner size="sm" />
          ) : (
            <span className={cn(
              phoneNumberError ? "text-error-700 dark:text-error-400" : "text-gray-900 dark:text-gray-100"
            )}>
              {currentPhoneNumber?.number || t('resources.assistants.telephony.no_phone_assigned')}
            </span>
          )}
        </div>
        {phoneNumberError && (
          <p className="mt-1.5 text-sm text-error-600 dark:text-error-400">
            {phoneNumberError}
          </p>
        )}
      </div>
    )
  }

  if (isEditing) {
    return (
      <div className="relative">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Input
              ref={inputRef}
              value={searchTerm}
              onChange={(value) => {
                setSearchTerm(value)
                setIsOpen(true)
              }}
              onFocus={() => setIsOpen(true)}
              placeholder={t('resources.assistants.telephony.search_phone_number')}
              className={cn("w-full", phoneNumberError && "border-error-500")}
              aria-label={t('resources.assistants.telephony.search_phone_number')}
              aria-invalid={!!phoneNumberError}
            />

            {/* Dropdown */}
            {isOpen && (
              <div
                ref={dropdownRef}
                className="absolute left-0 right-0 top-full z-50 mt-1 max-h-60 overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800"
              >
                {isLoadingList ? (
                  <div className="flex items-center justify-center py-4">
                    <LoadingSpinner size="sm" />
                  </div>
                ) : phoneNumbers.length === 0 ? (
                  <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                    {t('resources.assistants.telephony.no_phone_numbers')}
                  </div>
                ) : (
                  phoneNumbers.map((phone) => (
                    <button
                      key={phone.id}
                      type="button"
                      onClick={() => handleSelectPhoneNumber(phone)}
                      className={cn(
                        'flex w-full items-center px-4 py-3 text-left text-sm transition-colors',
                        'hover:bg-gray-100 dark:hover:bg-gray-700',
                        phone.id === phoneNumberId &&
                          'bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-300'
                      )}
                    >
                      <span className="font-medium">{phone.number}</span>
                      {phone.id === phoneNumberId && (
                        <CheckIcon className="ml-auto h-4 w-4" />
                      )}
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

        <Button
          type="button"
          color="tertiary"
          size="sm"
          onPress={handleCancel}
          aria-label={t('components.confirm_dialog.cancel')}
          className="h-10 w-10 p-0"
          iconLeading={XClose}
        />
        </div>
        {phoneNumberError && (
          <p className="mt-1.5 text-sm text-error-600 dark:text-error-400">
            {phoneNumberError}
          </p>
        )}
      </div>
    )
  }

  // Read-only mode with edit button
  return (
    <>
      <div>
        <div className="flex items-center gap-2">
          <div className={cn(
            "flex-1 rounded-lg border px-4 py-3",
            phoneNumberError 
              ? "border-error-500 bg-error-50 dark:border-error-500 dark:bg-error-900/10"
              : "border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800"
          )}>
            {isLoadingCurrent ? (
              <LoadingSpinner size="sm" />
            ) : (
              <span className={cn(
                phoneNumberError ? "text-error-700 dark:text-error-400" : "text-gray-900 dark:text-gray-100"
              )}>
                {currentPhoneNumber?.number || t('resources.assistants.telephony.no_phone_assigned')}
              </span>
            )}
          </div>
          <Button
            type="button"
            color="tertiary"
            size="sm"
            onPress={handleEditClick}
            aria-label={t('resources.assistants.telephony.edit_phone_number')}
            className="h-10 w-10 p-0"
            iconLeading={EditIcon}
          />
        </div>
        {phoneNumberError && (
          <p className="mt-1.5 text-sm text-error-600 dark:text-error-400">
            {phoneNumberError}
          </p>
        )}
      </div>

      {/* Confirmation Modal */}
      <Modal isOpen={showConfirm} onOpenChange={(open) => !open && setShowConfirm(false)}>
        <ModalContent>
          <ModalHeader onClose={() => setShowConfirm(false)}>{t('resources.assistants.telephony.change_phone_number')}</ModalHeader>
          <ModalBody>
            {isWaOnline ? (
              <div className="space-y-4">
                <div className="rounded-lg border border-warning-300 bg-warning-50 p-4 dark:border-warning-800 dark:bg-warning-900/20">
                  <p className="text-sm font-medium text-warning-900 dark:text-warning-200">
                    {t('resources.assistants.telephony.change_phone_wa_warning')}
                  </p>
                </div>
                <Checkbox
                  isSelected={waAckChecked}
                  onChange={setWaAckChecked}
                  aria-label={t('resources.assistants.telephony.change_phone_wa_ack')}
                >
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    {t('resources.assistants.telephony.change_phone_wa_ack')}
                  </span>
                </Checkbox>
              </div>
            ) : (
              <p className="text-gray-600 dark:text-gray-400">
                {t('resources.assistants.telephony.change_phone_confirm')}
              </p>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onPress={() => setShowConfirm(false)}>
              {t('components.confirm_dialog.cancel')}
            </Button>
            <Button onPress={handleConfirm} isDisabled={isWaOnline && !waAckChecked}>
              {t('components.confirm_dialog.confirm')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}
